import { GameRegistry, type GameDefinition } from './game-registry.js';

export interface GameDescriptor {
  readonly gameId: string;
  readonly displayName: string;
  readonly seats: readonly string[];
  readonly seatTeams?: Readonly<Record<string, string>>;
  readonly minPlayers: number;
  readonly maxPlayers: number;
  readonly defaultSeat?: string;
  readonly layoutId?: string;
  readonly discovery?: {
    readonly summary: string;
    readonly family: string;
    readonly playerLabel: string;
    readonly formatLabel: string;
  };
}

export interface GameCatalogEntry {
  readonly descriptor: GameDescriptor;
  readonly load: () => Promise<GameDefinition>;
}

/** Discovery is synchronous data; definitions are loaded only when explicitly requested. */
export class GameCatalog {
  private readonly entries = new Map<string, GameCatalogEntry>();
  private readonly definitions = new GameRegistry();
  private readonly pending = new Map<string, Promise<GameDefinition>>();

  constructor(entries: readonly GameCatalogEntry[]) {
    for (const entry of entries) {
      const descriptor = structuredClone(entry.descriptor);
      const { gameId, seats, minPlayers, maxPlayers, defaultSeat, seatTeams } = descriptor;
      if (
        !gameId ||
        !descriptor.displayName ||
        seats.length === 0 ||
        Array.from(seats).some((seat) => typeof seat !== 'string' || !seat) ||
        new Set(seats).size !== seats.length ||
        !Number.isSafeInteger(minPlayers) ||
        !Number.isSafeInteger(maxPlayers) ||
        minPlayers < 1 ||
        minPlayers > maxPlayers ||
        maxPlayers > seats.length ||
        (defaultSeat !== undefined && !seats.includes(defaultSeat)) ||
        (seatTeams !== undefined &&
          (Object.keys(seatTeams).length !== seats.length || seats.some((seat) => !seatTeams[seat])))
      ) {
        throw new Error(`Invalid game descriptor '${gameId}'`);
      }
      if (this.entries.has(gameId)) throw new Error(`Duplicate game descriptor '${gameId}'`);
      Object.freeze(descriptor.seats);
      if (descriptor.seatTeams) Object.freeze(descriptor.seatTeams);
      if (descriptor.discovery) Object.freeze(descriptor.discovery);
      this.entries.set(gameId, { descriptor: Object.freeze(descriptor), load: entry.load });
    }
  }

  list(): readonly GameDescriptor[] {
    return [...this.entries.values()].map((entry) => entry.descriptor);
  }

  get(gameId: string): GameDescriptor | undefined {
    return this.entries.get(gameId)?.descriptor;
  }

  load(gameId: string): Promise<GameDefinition> {
    const entry = this.entries.get(gameId);
    if (!entry) return Promise.reject(new Error(`Unknown or disabled game '${gameId}'`));
    const loaded = this.definitions.get(gameId);
    if (loaded) return Promise.resolve(loaded);
    const pending = this.pending.get(gameId);
    if (pending) return pending;
    const loading = Promise.resolve()
      .then(() => entry.load())
      .then((definition) => {
        this.validateDefinition(entry.descriptor, definition);
        this.definitions.register(definition);
        return definition;
      })
      .finally(() => this.pending.delete(gameId));
    this.pending.set(gameId, loading);
    return loading;
  }

  private validateDefinition(descriptor: GameDescriptor, definition: GameDefinition): void {
    const fields = ['gameId', 'displayName', 'minPlayers', 'maxPlayers', 'defaultSeat', 'layoutId'] as const;
    if (
      fields.some((field) => descriptor[field] !== definition[field]) ||
      descriptor.seats.length !== definition.seats.length ||
      descriptor.seats.some((seat, index) => definition.seats[index] !== seat) ||
      Object.keys(descriptor.seatTeams ?? {}).length !== Object.keys(definition.seatTeams ?? {}).length ||
      descriptor.seats.some((seat) => descriptor.seatTeams?.[seat] !== definition.seatTeams?.[seat])
    ) {
      throw new Error(`Game definition does not match descriptor '${descriptor.gameId}'`);
    }
  }
}
